import React, { useEffect, useMemo, useState } from 'react'
import MirsTable from 'components/general/table/Table'
import Label from "components/general/Label"
import Discipline from "components/presentation/Discipline";
import { useGetDisciplines, useGetH1 } from 'helpers/commonCodes';
import { useGetURL, useUrlFriendlyToParameters } from 'helpers/urlFriendly';
import Medal from 'components/presentation/Medal';
import SportCode from 'components/common/SportCode';
import Noc from 'components/presentation/Noc';
import { Stack } from 'react-bootstrap';
import { Link } from 'react-router-dom';

export default function PersonMedals({ data }) {
  let params = useUrlFriendlyToParameters()
  const getURL = useGetURL();
  const isTeam = !!(data?.team);
  const bioData = isTeam ? data?.team : data?.person
  const disciplines = useGetDisciplines(params)
  const { h1s } = useGetH1({ mirs_competition: params['mirs_competition'] });
  const [arrHasLinks, setArrHasLinks] = useState([]);

  useEffect(() => {
    const linksArray = [];
    h1s.filter(r => r.orisNo === 'N22')?.forEach((r) => {
      linksArray.push(r.disciplineCode);
    });
    setArrHasLinks(linksArray);
  }, [h1s]);


  const columnsMedals = useMemo(
    () => [
      ColumnMedalDiscipline(disciplines),
      ColumnMedalEvent(bioData, arrHasLinks, params, getURL),
      ColumnRank(bioData, isTeam),
      ColumnMedal(bioData)
    ], [arrHasLinks, disciplines, isTeam, bioData, getURL, params]);

  let hiddenColumns = []// hasMoreThanOneDisc ? [] : ['MedalsDiscipline']
  return (
    <>
      {bioData && bioData.registeredEvents &&
        <>
          <div id="events"><h2 className="wrsBiosDetail__h2" id="table-biomedals"><Label data="LabelEventsMedals" /></h2></div>
          < MirsTable
            data={bioData?.registeredEvents}
            columns={columnsMedals}
            initialSortBy={[{ id: 'Event', desc: false }]}
            hiddenColumns={hiddenColumns}
            ariaLabelledby={"table-biomedals"}
            id={`mirs-table-biomedals`}
          />
        </>
      }
    </>
  )
}

function ColumnMedalDiscipline(disciplines) {
  return (
    {
      Header: <Label data={"HeaderDiscipline"} />,
      accessor: 'disciplineCode',
      className: 'schedule-discipline',
      headerClassName: 'schedule-discipline',
      disableSortBy: true,
      id: 'MedalsDiscipline',
      Cell: (props) => {
        const row = props.cell.row.original
        const disciplineCode = getDisciplineCode(row?.event?.code);
        const description = disciplines?.disciplines?.find(d => d.code === disciplineCode)?.description
        return (
          <Discipline code={disciplineCode} description={description} showDescription={true} />
        )
      }
    }
  )
}

function ColumnMedalEvent(bioData, arrHasLinks, params, getURL) {
  let mirs_code = bioData?.code;
  let category = bioData?.mainFunction?.category;
  let mirs_noc = bioData?.organisation?.code;
  let organisationDescription = bioData?.organisation?.description;

  return (
    {
      Header: <Label data={"HeaderEvent"} />,
      accessor: 'event.order',
      headerClassName: 'text-start w-25',
      id: 'Event',
      disableSortBy: true,
      Cell: (props) => {
        const row = props.cell.row.original
        const event = row?.event
        const disciplineCode = getDisciplineCode(event?.code);
        let hasLink = arrHasLinks.find(discipline => discipline === disciplineCode);
        let team_code;
        if (category === 'A') {
          team_code = event?.rankings?.find(rank => rank?.participant?.athletes?.some(persons => persons?.person?.code === mirs_code))?.team_code;
          team_code = team_code ? team_code : bioData?.teamCodes?.find(itemTeamCode => itemTeamCode?.includes(event?.code?.substring(0, 10)));
        } else if (category === 'C') {
          team_code = event?.rankings?.find(rank => rank?.team_code.includes(mirs_noc))?.team_code;
        }
        params["mirs_code"] = team_code;
        let url = getURL(params, "team");
        return (
          <>
            <div>
              <span>{props.cell.row.original?.event?.longDescription}</span>
              <Stack direction='horizontal'>
                {bioData?.organisation &&
                  <>
                    <Noc {...bioData?.organisation} showImgAlt={false} />
                    {hasLink && team_code ? <Link to={url} >{organisationDescription}</Link> : <span>{organisationDescription}</span>}
                  </>
                }
              </Stack>
            </div>
          </>
        )
      }
    }
  )
};

function getDisciplineCode(eventCode) {
  return eventCode?.substring(0, 3);
}

function ColumnRank(bioData, isTeam) {
  return (
    {
      Header: <Label data={"HeaderRank"} />,
      accessor: 'rankings.rk_Rank',
      className: 'text-center',
      headerClassName: 'text-center w-20',
      disableSortBy: true,
      id: 'Rank',
      Cell: (props) => {
        const row = props.cell.row.original;
        const event = row?.event;
        const competitor = bioData?.code;
        const disciplineCode = getDisciplineCode(event?.code);
        let category = bioData?.mainFunction?.category;
        // for team discipline, we may display team bio or athlete bio;
        const ranking = event?.rankings?.find(r => {
          if (isTeam) {
            return r?.team_code === competitor;
          } else {
            if (category === "C") {
              return bioData?.coachTeamCodes?.split(",")?.map(t => t?.trim())?.includes(r?.team_code);
            } else {
              if (r?.athlete_code) {
                return r?.athlete_code === competitor;
              } else {
                return r?.teamAthletes?.find(ath => ath?.athlete_code === competitor);
              };
            };
          };
        });
        const rank = ranking?.rk_Rank;
        const rk_IRM = ranking?.rk_IRM;
        const rk_rankEqual = ranking?.rk_RankEqual;
        const rk_ResultType = ranking?.rk_ResultType;
        const rk_out = (rk_rankEqual) ? `=${rank}` : rank;

        return (
          <>
            {rk_ResultType?.match("IRM")
              ? <SportCode discipline={disciplineCode} entity='@IRM' code={rk_IRM} abbr={true} />
              : rk_out
            }
          </>
        )
      }
    }
  )
}

function ColumnMedal(bioData) {
  return (
    {
      Header: <Label data={"HeaderMedal"} />,
      accessor: 'location.locationOrder',
      className: 'medallist-medal',
      headerClassName: 'medallist-medal w-20',
      disableSortBy: true,
      id: 'Medal',
      Cell: (props) => {
        const row = props.cell.row.original;
        const eventCode = row?.event?.code;
        let category = bioData?.mainFunction?.category;
        // use category for coach only
        const personMedals = category === "C" ? bioData?.medalsCoach : bioData?.medals;
        const medal = personMedals?.find(m => m.event_code === eventCode);
        const medal_type = medal?.medal_type;
        return (
          medal_type && <Medal code={medal_type} />
        );
      }
    }
  );
}